import { useState, useEffect, useRef, useCallback } from "react";

/** Lista fechada de hooks para eco visual */
const HOOK_PHRASES = [
  "A conversa não terminou. Apenas começou.",
  "Volte quando estiver pronto para avançar.",
  "Hoje você está aqui por um motivo.",
  "A cada retorno, você se aproxima.",
];

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

/**
 * Ducking leve na música de fundo.
 * Reduz volume por downMs, depois retorna suavemente em upMs.
 * Nunca pausa. Se audioEl não existir, não faz nada.
 */
export function duckMusic(audioEl, options = {}) {
  const drop = options.drop ?? 0.10;
  const downMs = options.downMs ?? 300;
  const upMs = options.upMs ?? 750;

  if (!audioEl || typeof audioEl.volume === "undefined") return;

  const baseVolume = clamp(audioEl.volume, 0, 1);
  const targetVolume = clamp(baseVolume - drop, 0, 1);

  let rafId;
  const start = performance.now();

  function tick(now) {
    const elapsed = now - start;
    if (elapsed < downMs) {
      const t = elapsed / downMs;
      audioEl.volume = baseVolume + (targetVolume - baseVolume) * t;
    } else if (elapsed < downMs + upMs) {
      const t = (elapsed - downMs) / upMs;
      audioEl.volume = targetVolume + (baseVolume - targetVolume) * t;
    } else {
      audioEl.volume = baseVolume;
      return;
    }
    rafId = requestAnimationFrame(tick);
  }
  rafId = requestAnimationFrame(tick);

  return () => {
    if (rafId) cancelAnimationFrame(rafId);
  };
}

/**
 * Gate conservador: ativa revelação por parágrafos apenas se:
 * - EPIC_EXPERIENCE_ENABLED
 * - >= 2 parágrafos
 * - sem code blocks (```)
 * - sem tabelas markdown (|)
 * - sem listas longas (\n- repetido > 8)
 * - total >= 240 chars
 */
function passesEpicGate(text) {
  if (!text || typeof text !== "string") return false;

  const t = text.trim();
  if (t.length < 240) return false;
  if (/```[\s\S]*?```/m.test(t)) return false;
  if (/^\|.+\|$/m.test(t)) return false;
  const listMatches = t.match(/\n-\s/g);
  if (listMatches && listMatches.length > 8) return false;

  const paras = t.split(/\n\n+/).filter((p) => p.trim().length > 0);
  return paras.length >= 2;
}

/**
 * Divide texto em parágrafos (split por \n\n).
 * Se o último parágrafo casar com hook da lista fechada, separa como hook.
 */
function splitIntoParagraphsAndHook(text) {
  const paras = text.trim().split(/\n\n+/).filter((p) => p.trim().length > 0);
  if (paras.length === 0) return { main: [], hook: null };
  if (paras.length === 1) return { main: paras, hook: null };

  const last = paras[paras.length - 1].trim();
  const lastLower = last.toLowerCase();
  const isHook = HOOK_PHRASES.some((h) => lastLower === h.toLowerCase().trim());

  if (isHook && last.length < 80) {
    return {
      main: paras.slice(0, -1),
      hook: last,
    };
  }
  return { main: paras, hook: null };
}

const EPIC_ENABLED = false;
const PARAGRAPH_DELAY_MS = 210;
const HOOK_DELAY_MS = 420;

export default function EpicResponseRenderer({ text, audioRef }) {
  const [revealAll, setRevealAll] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [showHook, setShowHook] = useState(false);
  const timersRef = useRef([]);

  const { main, hook } = splitIntoParagraphsAndHook(text || "");
  const useEpic = EPIC_ENABLED && passesEpicGate(text || "");

  const handleRevealAll = useCallback((e) => {
    if (e) e.stopPropagation();
    setRevealAll(true);
  }, []);

  useEffect(() => {
    if (!useEpic) {
      setRevealAll(true);
      return () => {};
    }

    setRevealAll(false);
    setVisibleCount(0);
    setShowHook(false);

    duckMusic(audioRef?.current, { drop: 0.10, downMs: 300, upMs: 750 });

    const ids = [];
    main.forEach((_, i) => {
      const id = setTimeout(() => {
        setVisibleCount((c) => c + 1);
      }, i * PARAGRAPH_DELAY_MS);
      ids.push(id);
    });

    if (hook) {
      const hookId = setTimeout(() => {
        setShowHook(true);
      }, main.length * PARAGRAPH_DELAY_MS + HOOK_DELAY_MS);
      ids.push(hookId);
    }

    timersRef.current = ids;
    return () => {
      ids.forEach((id) => clearTimeout(id));
      timersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- main derived from text; avoid array ref churn
  }, [text, useEpic, main.length, hook, audioRef]);

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") handleRevealAll();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [handleRevealAll]);

  if (!useEpic || revealAll) {
    return <span className="epic-plain">{text}</span>;
  }

  return (
    <span
      className="epic-response-container"
      onClick={handleRevealAll}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleRevealAll()}
      aria-label="Clique para revelar todo o texto"
    >
      {main.slice(0, visibleCount).map((p, i) => (
        <span key={i} className="epic-paragraph">
          {p}
          {i < main.length - 1 && "\n\n"}
        </span>
      ))}
      {showHook && hook && (
        <span className="epic-hook">
          {"\n\n"}
          {hook}
        </span>
      )}
      {!revealAll && (visibleCount < main.length || (hook && !showHook)) && (
        <span className="epic-hint"> ··· (clique para revelar)</span>
      )}
    </span>
  );
}
